// Tasks (2026-10-12): a tracker for work someone has to handle at a later
// date, replacing Support's Azure DevOps board and its daily "scrub" email.
// See docs/decisions.md (2026-10-12).
//
// ONE definition for the form, the server's validation and the list: task
// types are data here. Adding a type is a new entry below plus a migration
// widening tasks.type's CHECK. Each field has a key (the API's and the
// column's name), a label, a kind and whether it is required:
//   text       a single line
//   multiline  several lines
//   datetime   a calendar date with an optional time (America/New_York, as
//              entered, never converted); the field stores two keys,
//              `${key}Date` and `${key}Time`, and a time needs a date.
//              Its `section` is the heading the form shows above it, and its
//              `triggers` the conditions that can stand in for the date
//              (stored as dueTrigger, never together with a date)
//
// Order Number (2026-10-13) is the field formerly labelled Order Details: the
// stored key stays orderDetails.
//
// No status and no delete yet, deliberately: more is coming.

import { addBusinessDays, TASK_BUSINESS_DAYS_AHEAD } from './business-days.js'
import { returnStagePathKey } from './return-refund-overdue.js'

export const TASK_TIMEZONE = 'America/New_York'

export const TASK_TYPES = Object.freeze([
  Object.freeze({
    key: 'other',
    label: 'Other',
    fields: Object.freeze([
      Object.freeze({ key: 'orderDetails', label: 'Order Number', kind: 'text', required: true }),
      Object.freeze({ key: 'description', label: 'Description', kind: 'multiline', required: true }),
      Object.freeze({ key: 'actionNeededBy', label: 'Action Needed By Date', section: 'When Is Action Needed By', kind: 'datetime', required: false, bases: Object.freeze(['fifth_business_day', 'fifth_business_day_from_last_scan']), triggers: Object.freeze(['replacement_tracking_available', 'return_document_created']) }),
    ]),
  }),
])

// A condition a task can be due on instead of a date (2026-10-13), stored in
// tasks.due_trigger. The date sweep never delivers a task that has one (it
// has no date); each trigger has its own job (2026-10-14,
// docs/decisions/2026-10-14-task-flows-reset.md):
//   replacement_tracking_available  services/task-replacement-tracking-sweep.js
//                                   (every 4 hours), intent
//                                   update_replacement_tracking (eBay) or other
//   return_document_created         services/task-return-document-sweep.js
//                                   (every 12 hours), intent
//                                   confirm_refund_value
export const TASK_DUE_TRIGGERS = Object.freeze({
  replacement_tracking_available: Object.freeze({
    label: 'When Replacement Order Tracking is Available',
    display: 'When replacement tracking is available',
  }),
  return_document_created: Object.freeze({
    label: 'Return Document Created',
    display: 'When a return document is created',
  }),
})
export function taskDueTriggerDisplay(trigger) {
  return TASK_DUE_TRIGGERS[trigger]?.display ?? null
}

// The change-log line when the date came from the "5th Business Day" box.
// The form's labels changed on 2026-10-15; earlier entries keep their words.
export const TASK_FIFTH_BUSINESS_DAY_NOTE = 'Action Needed By set from 5 Business Days From Now'

// The boxes that FILL the date (2026-10-13), stored in tasks.due_basis so the
// edit form shows what was used. With the replacement-tracking trigger they
// are three mutually exclusive, optional options, in this order.
export const TASK_DUE_BASES = Object.freeze({
  // Form labels (2026-10-15); the stored keys never change.
  fifth_business_day: Object.freeze({ label: '5 Business Days From Now' }),
  fifth_business_day_from_last_scan: Object.freeze({ label: '5 Business Days From Last Tracking Scan' }),
})
export const FIFTH_BUSINESS_DAY = 'fifth_business_day'
export const FIFTH_BUSINESS_DAY_FROM_LAST_SCAN = 'fifth_business_day_from_last_scan'
// "10/10/2026" from "2026-10-10".
export function formatTaskDate(date) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ''))
  return m ? `${m[2]}/${m[3]}/${m[1]}` : ''
}
// The change-log note for a date the option filled (or none).
export function taskDueBasisNote(dueBasis, lastScanDate) {
  if (dueBasis === FIFTH_BUSINESS_DAY) return TASK_FIFTH_BUSINESS_DAY_NOTE
  if (dueBasis === FIFTH_BUSINESS_DAY_FROM_LAST_SCAN) return `Action Needed By set from 5 Business Days From Last Tracking Scan (last scan ${formatTaskDate(lastScanDate)})`
  return null
}

export function taskType(key) {
  return TASK_TYPES.find((t) => t.key === key) ?? null
}

export function taskTypeLabel(key) {
  return taskType(key)?.label ?? key
}

// The display ID: "T" + the numeric id, no dash (T1001). The only place it is
// built; nothing stores it.
export function taskDisplayId(id) {
  return id == null ? '' : `T${id}`
}

// "T1001", "t1001" or "1001" -> 1001; anything else -> null.
export function parseTaskId(value) {
  const m = /^\s*[Tt]?(\d{1,9})\s*$/.exec(String(value ?? ''))
  return m ? Number(m[1]) : null
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

function isRealDate(value) {
  if (!DATE_RE.test(value)) return false
  const [y, m, d] = value.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

function blank(v) {
  return v == null || String(v).trim() === ''
}

// The columns-shaped values a field set produces, keyed as the API speaks:
// orderDetails, description, actionNeededByDate, actionNeededByTime, and
// dueTrigger for a datetime field that has triggers.
export function taskFieldKeys(type) {
  return (taskType(type)?.fields ?? []).flatMap((f) => (f.kind === 'datetime'
    ? [`${f.key}Date`, `${f.key}Time`, ...(f.bases?.length ? ['dueBasis', 'lastScanDate'] : []), ...(f.triggers?.length ? ['dueTrigger'] : [])]
    : [f.key]))
}

// Validates input against a type's definition. partial: an edit, where an
// absent key means "unchanged" (but a present blank required field is still
// refused). Returns { values, errors }: values holds only the type's own keys
// (trimmed; blank optional values become null), errors maps a key to a
// message. A time without a date is refused; so is a date or time that isn't
// real.
// now: for "today" in America/New_York (a Last Scan Date can't be after it).
export function validateTaskInput(type, input = {}, { partial = false, existing = {}, now = new Date() } = {}) {
  const def = taskType(type)
  if (!def) return { values: {}, errors: { type: 'Choose a task type.' } }
  const values = {}
  const errors = {}
  for (const field of def.fields) {
    if (field.kind === 'datetime') {
      const dk = `${field.key}Date`
      const tk = `${field.key}Time`
      const hasD = Object.prototype.hasOwnProperty.call(input, dk)
      const hasT = Object.prototype.hasOwnProperty.call(input, tk)
      const hasOption = ['dueBasis', 'lastScanDate', 'dueTrigger'].some((k) => Object.prototype.hasOwnProperty.call(input, k))
      if (partial && !hasD && !hasT && !hasOption) continue
      const date = hasD ? (blank(input[dk]) ? null : String(input[dk]).trim()) : (existing[dk] ?? null)
      const time = hasT ? (blank(input[tk]) ? null : String(input[tk]).trim()) : (existing[tk] ?? null)
      if (date && !isRealDate(date)) errors[dk] = `${field.label}: enter a real date (YYYY-MM-DD).`
      if (time && !TIME_RE.test(time)) errors[tk] = `${field.label}: enter a time as HH:MM.`
      if (time && !date) errors[tk] = `${field.label}: a time needs a date.`
      // A trigger stands in for the date (2026-10-13): one of the field's own,
      // and never together with a date or time.
      let trigger = null
      if (field.triggers?.length) {
        const hasTr = Object.prototype.hasOwnProperty.call(input, 'dueTrigger')
        trigger = hasTr ? (blank(input.dueTrigger) ? null : String(input.dueTrigger).trim()) : (existing.dueTrigger ?? null)
        if (trigger && !field.triggers.includes(trigger)) errors.dueTrigger = 'Choose a valid Action Needed By option.'
        else if (trigger && (date || time)) errors.dueTrigger = 'A task has either an Action Needed By date or a trigger, not both.'
        if (!partial || hasTr) values.dueTrigger = trigger
      }
      // The option that filled the date (2026-10-13): one of the field's own,
      // never with a trigger, always with a date. "From Last Scan" needs its
      // Last Scan Date (a real date, not after today in America/New_York), and
      // its date is the 5th business day after it; a Last Scan Date never
      // stands without its option.
      if (field.bases?.length) {
        const hasB = Object.prototype.hasOwnProperty.call(input, 'dueBasis')
        const hasL = Object.prototype.hasOwnProperty.call(input, 'lastScanDate')
        const basis = hasB ? (blank(input.dueBasis) ? null : String(input.dueBasis).trim()) : (existing.dueBasis ?? null)
        const lastScan = hasL ? (blank(input.lastScanDate) ? null : String(input.lastScanDate).trim()) : (existing.lastScanDate ?? null)
        if (basis && !field.bases.includes(basis)) errors.dueBasis = 'Choose a valid Action Needed By option.'
        else if (basis && trigger) errors.dueBasis = 'Choose only one Action Needed By option.'
        else if (basis && !date) errors[dk] = `${field.label} is required with that option.`
        if (lastScan && basis !== FIFTH_BUSINESS_DAY_FROM_LAST_SCAN) errors.lastScanDate = 'A Last Scan Date goes only with 5 Business Days From Last Tracking Scan.'
        if (basis === FIFTH_BUSINESS_DAY_FROM_LAST_SCAN) {
          if (!lastScan) errors.lastScanDate = 'Last Scan Date is required.'
          else if (!isRealDate(lastScan)) errors.lastScanDate = 'Last Scan Date: enter a real date (YYYY-MM-DD).'
          else if (lastScan > easternDate(now)) errors.lastScanDate = "Last Scan Date can't be in the future."
          else if (date && date !== addBusinessDays(lastScan, TASK_BUSINESS_DAYS_AHEAD)) errors[dk] = `${field.label} must be the 5th business day after the Last Scan Date.`
        }
        if (!partial || hasB) values.dueBasis = basis
        if (!partial || hasL) values.lastScanDate = lastScan
      }
      if (field.required && !date && !trigger) errors[dk] = `${field.label} is required.`
      values[dk] = date
      values[tk] = time
      continue
    }
    const has = Object.prototype.hasOwnProperty.call(input, field.key)
    if (partial && !has) continue
    const value = blank(input[field.key]) ? null : String(input[field.key]).trim()
    if (field.required && !value) errors[field.key] = `${field.label} is required.`
    values[field.key] = value
  }
  return { values, errors }
}

// Order references CXHub recognises in free text (the same shapes the M365
// extraction and the master-index routing use): an ERP order number (ORD…,
// AMORD…, TRQORD… + digits), a PO / channel order number (2D… / 2M…, letters
// and digits), and the hyphenated eBay order id (12-34567-89012). Bounded by a
// non-alphanumeric character on each side, so AMORD is matched whole.
const ORDER_REF_RE = /(?<![A-Za-z0-9])((?:TRQ|AM)?ORD\d{5,}|2[DM][A-Z0-9]{6,}|\d{2}-\d{5}-\d{5})(?![A-Za-z0-9])/gi

// [{ text, ref }] in order: ref is the upper-cased reference for a match, null
// for the plain text between matches.
export function splitOrderRefs(text) {
  const s = String(text ?? '')
  const out = []
  let last = 0
  for (const m of s.matchAll(ORDER_REF_RE)) {
    if (m.index > last) out.push({ text: s.slice(last, m.index), ref: null })
    out.push({ text: m[0], ref: m[0].toUpperCase() })
    last = m.index + m[0].length
  }
  if (last < s.length) out.push({ text: s.slice(last), ref: null })
  return out
}

export function orderRefsIn(text) {
  return [...new Set(splitOrderRefs(text).filter((p) => p.ref).map((p) => p.ref))]
}

// Today's calendar date in America/New_York, as YYYY-MM-DD.
export function easternDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TASK_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now)
  const get = (t) => parts.find((p) => p.type === t)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}

// The list's date filters are presets (2026-10-13), resolved by the server
// against today in America/New_York; absent means All. Last 7 / Last 30 Days
// are today plus the 6 / 29 days before it. Action Needed By "Today" includes
// overdue dates; "Next 7 Days" is tomorrow through today + 7; "No date" is a
// task without one.
export const TASK_CREATED_PRESETS = Object.freeze([
  Object.freeze({ key: 'today', label: 'Today' }),
  Object.freeze({ key: 'yesterday', label: 'Yesterday' }),
  Object.freeze({ key: 'last7', label: 'Last 7 Days' }),
  Object.freeze({ key: 'last30', label: 'Last 30 Days' }),
])
export const TASK_ACTION_PRESETS = Object.freeze([
  Object.freeze({ key: 'today', label: 'Today (includes overdue)' }),
  Object.freeze({ key: 'tomorrow', label: 'Tomorrow' }),
  Object.freeze({ key: 'next7', label: 'Next 7 Days' }),
  Object.freeze({ key: 'none', label: 'No date' }),
])

// The list's sorts: by Action Needed By (the default, ascending) or by Created
// On, each either way. A task with no Action Needed By Date is at the top in
// both Action Needed By directions.
export const TASK_SORTS = Object.freeze({ actionAsc: 'action_asc', actionDesc: 'action_desc', createdAsc: 'created_asc', createdDesc: 'created_desc' })
export const DEFAULT_TASK_SORT = TASK_SORTS.actionAsc
export function normalizeTaskSort(value) {
  return Object.values(TASK_SORTS).includes(value) ? value : DEFAULT_TASK_SORT
}

// A task whose Action Needed By Date is today or earlier (Eastern) is due.
// No date: never highlighted.
export function isTaskDue(task, now = new Date()) {
  const date = task?.actionNeededByDate ?? null
  return Boolean(date) && date <= easternDate(now)
}

// Who can use Tasks (2026-10-13, Phase 1): admins, and a csr with the Roles
// page's Tasks permission (agent_permissions.tasks). Support Permissions,
// Support Email and Support Voice no longer grant it. The same rule covers
// the Tasks page, every Tasks route and every task contact anywhere (an
// exclusive visibility gate, services/agent-visibility.js). Anyone who can
// use it can view and edit every task.
export function canUseTasks({ isAdmin = false, permissions = null } = {}) {
  if (isAdmin) return true
  return Boolean(permissions?.tasks)
}

// A task's life (Phase 1): scheduled until due, in_queue while its contact
// is in the Queue, closed. Labels for the Tasks page's Status column.
export const TASK_STATUSES = Object.freeze({ scheduled: 'scheduled', inQueue: 'in_queue', closed: 'closed' })
export const TASK_STATUS_LABELS = Object.freeze({ scheduled: 'Waiting', in_queue: 'In Queue', closed: 'Closed' })

// When a dated task is due (Phase 1, date-based only): 07:00
// America/New_York on its Action Needed By Date, or its exact time when one
// was entered, every day of the week. A task with no date is never due.
export const TASK_DEFAULT_DUE_TIME = '07:00'

// The task contact's subject: "T1001: " + the description's first line.
export function taskContactSubject(task) {
  const first = String(task?.description ?? '').split(/\r?\n/).map((l) => l.trim()).find(Boolean) ?? ''
  return `${taskDisplayId(task?.id)}: ${first}`.slice(0, 200)
}

// The Subject a task's email response starts with (2026-10-13): empty, so the
// agent types it; never the contact's own subject ("T1001: …") and never
// "Re:". A standard subject line will be configured here later; the reply
// box seeds from this and nowhere else.
export function taskResponseSubject(/* task */) {
  return ''
}

// The channel whose order header a conversation gets (2026-10-13). Every
// channel is its own, except a task contact, which takes its ORDER's channel
// from the stored TRQ client code: a Website order (1AW / AMW) the email
// header, an eBay order (1AE / AME) the eBay header; an unrouted task (or any
// other client) none (null). Shared by the order-summary routes and App.jsx's
// layout, so the server's fields and the UI's layout always agree.
export function headerChannelFor(conversation) {
  const channel = conversation?.channel ?? null
  if (channel !== 'task') return channel
  const code = String(conversation?.trqClientCode ?? '').toUpperCase()
  if (code === '1AW' || code === 'AMW') return 'm365_email'
  if (code === '1AE' || code === 'AME') return 'ebay'
  return null
}

// The task flows' moved / not-moved choice (2026-10-15): when CXHub can't
// tell whether a package moved, a task_path_choice step asks the agent, and
// each later step carries its taskPath ('moved' / 'not_moved'). The choice is
// recorded on the suggestion's apiCalls (routes/ai.js's
// POST /api/ai/suggestions/:id/task-path), so a reload and the Activity Log
// show it and who made it. The latest entry wins.
//
// The same choice asks a Return Status contact whether the return is in
// transit or delivered (2026-10-21, server/src/ai/return-status.js): an
// option may then need a date (option.dateRequired), recorded with the choice
// as taskPathDate (YYYY-MM-DD), and the step may carry a preselection
// (params.preselected: { value, reason, date? }) that counts as the choice
// until the agent records one.
export function taskPathChoiceFrom(apiCalls) {
  for (let k = (apiCalls ?? []).length - 1; k >= 0; k--) {
    const c = apiCalls[k]
    if (c?.taskPathChoice) return { choice: c.taskPathChoice, date: c.taskPathDate ?? null, by: c.actedBy ?? null, at: c.at ?? null, stepIndex: c.stepIndex ?? null }
  }
  return null
}
// The choice step of a suggestion, or null.
export function taskPathChoiceStep(proposedActions) {
  return (proposedActions ?? []).find((a) => a?.type === 'task_path_choice') ?? null
}
// The choice in effect: the agent's recorded one, else the step's
// preselection. { choice, date, by, preselected } or null.
export function effectiveTaskPathChoice(suggestion) {
  const recorded = taskPathChoiceFrom(suggestion?.modelMeta?.apiCalls)
  if (recorded) return { ...recorded, preselected: false }
  const pre = taskPathChoiceStep(suggestion?.proposedActions)?.params?.preselected
  return pre?.value ? { choice: pre.value, date: pre.date ?? null, by: null, at: null, stepIndex: null, preselected: true, reason: pre.reason ?? null } : null
}
// Whether the chosen option still needs its date.
export function taskPathDateMissing(step, choice) {
  const option = (step?.params?.options ?? []).find((o) => o.value === choice?.choice)
  return Boolean(option?.dateRequired) && !isIsoDate(choice?.date)
}
export function isIsoDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v ?? ''))
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}
// A step that belongs to a path not (yet) chosen: never Processed or shown.
// proposedActions (2026-10-27, the sales-hold path choice): when given, the
// choice step's preselection counts as the choice until one is recorded, as
// it does in the panel (effectiveTaskPathChoice()).
// A Return Status stage "delivered" past the refund window (2026-10-29,
// return-refund-overdue.js) runs the overdue path's steps instead: the key
// is returnStagePathKey(), from the choice and its date.
export function isTaskPathBlocked(action, apiCalls, proposedActions = null) {
  if (!action?.taskPath) return false
  if (!proposedActions) return taskPathChoiceFrom(apiCalls)?.choice !== action.taskPath
  return effectiveTaskPathKey({ modelMeta: { apiCalls }, proposedActions }) !== action.taskPath
}
// The path that is on for a suggestion (the choice, or the overdue refund's
// path for a delivered return past its window), or null.
export function effectiveTaskPathKey(suggestion, today = undefined) {
  const effective = effectiveTaskPathChoice(suggestion)
  if (!effective) return null
  return returnStagePathKey(taskPathChoiceStep(suggestion?.proposedActions), effective, today)
}
// A suggestion still waiting for the agent's choice: never Sent.
// A Return Status stage choice also needs its date (2026-10-21).
export function needsTaskPathChoice(suggestion) {
  const step = taskPathChoiceStep(suggestion?.proposedActions)
  if (!step) return false
  const choice = effectiveTaskPathChoice(suggestion)
  return !choice || taskPathDateMissing(step, choice)
}
